import { useMemo, useState } from "react";
import { Disclosure } from "@/components/Disclosure";
import { FiltroArticulos } from "./components/FiltroArticulos";
import { IndicadoresCantidades } from "./components/IndicadoresCantidades";
import { TablaArticulos } from "./components/TablaArticulos";
import { TablaOrdenes } from "./components/TablaOrdenes";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useArticulos, type ArticuloFabricacion, type TiposSeleccionFiltroStock } from "@/hooks/Api/Articulos";
import { useOrdenes } from "@/hooks/Api/OrdenesFabricacion";

export function GestionArticulosPage() {
	const [filtroStock, setFiltroStock] = useState<TiposSeleccionFiltroStock>("todo");
	const [filtroFamilia, setFiltroFamilia] = useState<string[]>([]);
	const [filtroSubfamilia, setFiltroSubfamilia] = useState<string[]>([]);
	const [filtroTipoArticulo, setFiltroTipoArticulo] = useState<string[]>([]);

	const { data: articulos, error: errorArticulos, isPending: articulosPending } = useArticulos({
		idsFamilia: filtroFamilia,
		idsSubFamilia: filtroSubfamilia,
	});
	const { data: OFs, error: errorOFs, isPending: OFsPending } = useOrdenes({
		idsFamilia: filtroFamilia,
		idsSubFamilia: filtroSubfamilia,
	});

	const isInStock = (art: ArticuloFabricacion) => art.stock && art.stock > 0 && (!art.min || art.stock > art.min) && (!art.max || art.max == 0 || art.stock < art.max);
	const isUnderMin = (art: ArticuloFabricacion) => ((art.stock ?? 0) - (art.pendienteSalida ?? 0)) < (art.min ?? 0);
	const isUnderMin_Request = (art: ArticuloFabricacion) => ((art.stock ?? 0) - (art.pendienteSalida ?? 0) + (art.pendienteEntrada ?? 0)) < (art.min ?? 0);;
	const isOverMax = (art: ArticuloFabricacion) => art.max && art.max > 0 && ((art.stock ?? 0) + (art.pendienteEntrada ?? 0) - (art.pendienteSalida ?? 0)) > art.max;
	const isNoStock = (art: ArticuloFabricacion) => ((art.stock ?? 0) - (art.pendienteSalida ?? 0)) < 0;
	const isNoStock_Request = (art: ArticuloFabricacion) => ((art.stock ?? 0) - (art.pendienteSalida ?? 0) + (art.pendienteEntrada ?? 0)) < 0;

	const filteredData = useMemo(() => {
		if (!articulos) return articulos;
		let filtered = articulos;
		if (filtroTipoArticulo.length != 0)
			filtered = articulos.filter(art => filtroTipoArticulo.includes(String(art.tipoArticulo)));

		if (filtroStock === "enStock") filtered = filtered.filter(isInStock);
		else if (filtroStock === "minimo") filtered = filtered.filter(isUnderMin);
		else if (filtroStock === "minimo-request") filtered = filtered.filter(isUnderMin_Request);
		else if (filtroStock === "maximo") filtered = filtered.filter(isOverMax);
		else if (filtroStock === "roturaStock") filtered = filtered.filter(isNoStock);
		else if (filtroStock === "roturaStock-request") filtered = filtered.filter(isNoStock_Request);

		return filtered;
	}, [articulos, filtroTipoArticulo, filtroStock]);

	const counts = useMemo(() => {
		if (!articulos) return { todos: "", bajoMinimo: "", roturaStock: "", ofCount: "" };

		const count = { todos: articulos.length, bajoMinimo: 0, roturaStock: 0, ofCount: OFs?.length ?? 0 };
		articulos.forEach(art => {
			if (art.stock == null) return;

			if (isUnderMin(art)) {
				count.bajoMinimo++;
				if (isNoStock(art)) count.roturaStock++;
			}
		});

		return count;
	}, [articulos, OFs]);


	return (
		<div id="content" className="content" >
			<title>Piher | Gestión de Artículos</title>

			<Breadcrumb
				sectionTitle="Gestión de Artículos"
				links={[
					{ label: "Gestión de Artículos", active: true }
				]}
			/>

			<div className="box-shadow panel-box panel-info" style={{ display: "none" }}>
				<h4><strong>Info</strong></h4>
				Aquí podras encontrar un listado con información sobre la producción de ordenes de fabricación. Desde el botón + podras añadir una orden a la serie.
			</div>


			<Disclosure style={{ marginBottom: 20 }}>
				{/* First element is the summary, second is the content */}
				<h4 className="panel-title panel-heading senalar">
					Filtro
				</h4>
				<FiltroArticulos
					filtroStock={filtroStock}
					setFiltroStock={setFiltroStock}

					familia={filtroFamilia}
					setFamilia={setFiltroFamilia}

					subfamilia={filtroSubfamilia}
					setSubfamilia={setFiltroSubfamilia}

					tipoArticulo={filtroTipoArticulo}
					setTipoArticulo={setFiltroTipoArticulo}
				/>
			</Disclosure>

			<IndicadoresCantidades
				data={counts}
				setFiltroStock={setFiltroStock}
			/>

			<div>
				<h1 style={{ marginBottom: 0 }}> Artículos </h1>
				<TablaArticulos
					data={filteredData}
					error={errorArticulos}
					isPending={articulosPending}
				/>
			</div>

			<div id="datatableOrdenes">
				<h1 style={{ marginBottom: 0 }}> Ordenes de Fabricacion </h1>
				<TablaOrdenes
					data={OFs ?? []}
					error={errorOFs}
					isPending={OFsPending}
				/>
			</div>
		</div>
	);
}